import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMAGES, maskLine, EASE } from "@/lib/motion";

const HEADLINE = ["AGRO", "TRADE", "GLOBAL"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const hasBg = Boolean(IMAGES.heroWheat);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#2A1B0E]"
    >
      {/* Parallax background — real image if provided, else textured brand plate */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
      >
        {hasBg ? (
          <img
            src={IMAGES.heroWheat}
            alt="Agro Trade Global hero"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="relative h-full w-full bg-[#3D2817]">
            {/* Warm radial glow */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse at 70% 90%, rgba(224,169,109,0.35) 0%, rgba(61,40,23,0) 55%)",
              }}
            />
            {/* Diagonal weave pattern */}
            <div
              className="absolute inset-0 opacity-25"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, transparent 0 22px, rgba(240,230,192,0.05) 22px 23px)",
              }}
            />
          </div>
        )}
      </motion.div>
      {hasBg && (
        <>
          <motion.div
            style={{ opacity: overlayOpacity }}
            className="absolute inset-0 -z-10 bg-[#2A1B0E]"
          />
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#2A1B0E] via-transparent to-[#2A1B0E]/40" />
        </>
      )}

      <motion.div
        style={{ y: textY }}
        className="relative mx-auto flex min-h-screen max-w-[1400px] flex-col justify-end px-6 pb-20 pt-40 lg:px-12 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mb-6 text-xs md:text-sm uppercase tracking-[0.4em] text-[#E6D9A8]"
          data-testid="hero-overline"
        >
          Animal Feed Ingredients &nbsp;/&nbsp; Spices &nbsp;/&nbsp; Tea
        </motion.p>

        <h1
          data-testid="hero-headline"
          aria-label="Agro Trade Global"
          className="font-display leading-[0.82] tracking-tighter text-[#F5EFD6]"
        >
          {HEADLINE.map((word, i) => (
            <span key={word} className="reveal-mask">
              <motion.span
                className="block text-[19vw] sm:text-[16vw] lg:text-[13rem]"
                variants={maskLine}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.95, ease: EASE, delay: 0.4 + i * 0.16 }}
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col gap-8 border-t border-[#E6D9A8]/30 pt-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9, ease: EASE }}
            className="max-w-xl text-base md:text-lg leading-relaxed text-[#E6D9A8]"
          >
            <span className="block font-display text-2xl md:text-3xl italic text-[#F5EFD6] mb-3">
              Nature&apos;s Best. World&apos;s Finest.
            </span>
            Your bridge to North-East India&apos;s finest agricultural
            treasures. We don&apos;t just source&mdash;we partner.
          </motion.p>

          <motion.a
            href="#feed"
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.65, duration: 0.9, ease: EASE }}
            className="group inline-flex items-center gap-3 self-start rounded-full border border-[#E6D9A8] px-7 py-3.5 text-sm uppercase tracking-[0.18em] text-[#F5EFD6] transition-colors duration-300 hover:bg-[#F5EFD6] hover:text-[#3D2817]"
          >
            Explore Our Products
            <ArrowDownRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
};
